import { defaultAllSelection } from "services/constants/selections";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import * as Mui from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import dayjs from "dayjs";
import { useFetch } from "services/hooks/useFetch";
import { CustomAutoComplete } from "components/CustomComponents";
import { designColors, designRadii } from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";
import sessionHandling from "services/utils/notificationUtils";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const SELECTED_BG = "linear-gradient(180deg, rgba(20,184,166,0.18) 0%, rgba(255,255,255,0.98) 100%)";
const SELECTED_BORDER = "rgba(15,118,110,0.45)";
const CALENDAR_LEGEND = [
  { label: "Present", color: "#10B981", statuses: ["present"], codes: ["P"] },
  { label: "Absent", color: "#EF4444", statuses: ["absent"], codes: ["A"] },
  { label: "On Leave", color: "#2563EB", statuses: ["on leave", "leave"], codes: ["L"] },
  { label: "Holiday", color: "#A855F7", statuses: ["holiday"], codes: ["H"] },
  { label: "Week Off (WO)", color: "#94A3B8", statuses: ["week off", "weekoff", "week off (wo)"], codes: ["W", "WO"] },
];

export const getCalendarStatus = (entry) => {
  if (!entry) return undefined;
  const status = (entry.status || "").trim().toLowerCase().replace(/[-\s]+/g, " ");
  const code = (entry.legend || "").trim().toUpperCase();
  return CALENDAR_LEGEND.find((item) => item.statuses.includes(status)) ||
    CALENDAR_LEGEND.find((item) => item.codes.includes(code));
};

const NOT_MARKED = { status: "Not Marked", legend: "", colour_code: designColors.slate300 };

export const isUnavailableLeaveDate = (entry) => {
  const status = entry?.status?.trim().toLowerCase().replace(/[-\s]+/g, " ");
  const legend = entry?.legend?.trim().toUpperCase();
  return ["on leave", "leave", "week off", "weekoff", "week off (wo)", "holiday"].includes(status) ||
    ["L", "W", "WO", "H"].includes(legend);
};

// Day-type choices for a selected date. There used to be a 4th "Full Day
// (Not counted)" option here that manually zeroed out business_days for
// Week Off/Holiday days — that was a client-side stand-in for the
// server-side validate-leave-request check (see validateOneDate below),
// which is now wired back in and returns the authoritative business_days
// (0 for a non-working day) on its own, so the manual option is gone and
// every day just defaults to "full".
export const DAY_TYPE_OPTIONS = [
  { value: "full", label: "Full Day" },
  { value: "half_am", label: "Half Day (AM)" },
  { value: "half_pm", label: "Half Day (PM)" },
];

export const dayTypeToBusinessDays = (dayType) => {
  if (dayType === "half_am" || dayType === "half_pm") return 0.5;
  return 1;
};

const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== "string" || hex[0] !== "#") return hex;
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  const bigint = parseInt(full, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Calendar-driven date picker for the leave Apply panel. Each day cell is
// mapped from attendance-timeline data to the fixed legend below, so an approver can see whether the
// employee was Present/Absent/On Leave/etc. before deciding which days to
// apply leave for. Selection is a set of arbitrary (not necessarily
// contiguous) dates, each with its own day type — this is intentionally
// different from the old single from/to range + one shared half-day flag.
export const LeaveDateCalendar = ({
  domain,
  token,
  topbarCompanyId,
  userId,
  departmentId,
  department,
  departmentName,
  requestingUserId,
  leaveTypeId,
  selectedDates,
  onChange,
  // Bounds ("YYYY-MM-DD") of the currently selected leave year — when known,
  // the month picker only offers months inside that year instead of an
  // arbitrary rolling window unrelated to which year's balances apply.
  leaveYearStartDate,
  leaveYearEndDate,
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => dayjs(selectedDates[0]?.date || undefined).startOf("month"));
  const previousUserIdRef = useRef(userId);
  const validationRequestsRef = useRef(new Map());

  // Selection belongs to one employee at a time — switching employees starts over.
  useEffect(() => {
    if (previousUserIdRef.current !== userId) onChange([]);
    previousUserIdRef.current = userId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const validateOneDate = useCallback(
    async (dateStr, isHalfDay) => {
      if (!leaveTypeId || !userId) return { validationError: "Select an employee and leave type to validate this date." };
      try {
        const response = await fetch(`${domain}validate-leave-request`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify({
            user: userId,
            leave_type_id: leaveTypeId,
            from_date: dateStr,
            to_date: dateStr,
            is_half_day: isHalfDay,
          }),
        });
        const res = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (response.status === 409) sessionHandling();
          return { validationError: res.error || res.errors?.[0]?.message || res.message || "Unable to validate this date." };
        }
        return {
          businessDays: res?.business_days ?? (isHalfDay ? 0.5 : 1),
          isWeekoffApplied: Boolean(res?.is_weekoff_applied),
          isHolidayApplied: Boolean(res?.is_holiday_applied),
          sandwichApplied: Boolean(res?.sandwich_applied),
          validationMessage: res?.message || "",
          validationError: res?.sandwich_violation ? res.message || "This date violates the leave policy." : "",
          validatedFromDate: res?.from_date || dateStr,
          validatedToDate: res?.to_date || dateStr,
        };
      } catch (error) {
        return { validationError: "Unable to validate this date. Please retry." };
      }
    },
    [domain, leaveTypeId, token, userId],
  );

  const validateSelectedDate = useCallback((entry) => {
      if (!userId || !leaveTypeId) return;
      const requests = validationRequestsRef.current;
      const validationKey = JSON.stringify([userId, leaveTypeId, entry.date, entry.dayType]);
      if (entry.validationKey === validationKey || requests.has(validationKey)) return;
      const request = {};
      requests.set(validationKey, request);
      validateOneDate(entry.date, entry.dayType !== "full").then((result) => {
        if (requests.get(validationKey) !== request) return;
        onChange((previous) => previous.map((date) =>
          date.date === entry.date && date.dayType === entry.dayType
            ? { ...date, ...result, validationKey }
            : date,
        ));
      });
  }, [userId, leaveTypeId, validateOneDate, onChange]);

  useEffect(() => {
    const requests = validationRequestsRef.current;
    const activeKeys = new Set(selectedDates.map((entry) => JSON.stringify([userId, leaveTypeId, entry.date, entry.dayType])));
    for (const key of requests.keys()) {
      if (!activeKeys.has(key)) requests.delete(key);
    }
    selectedDates.forEach(validateSelectedDate);
  }, [selectedDates, userId, leaveTypeId, validateSelectedDate]);

  useEffect(() => () => validationRequestsRef.current.clear(), []);

  const monthStart = currentMonth.startOf("month");
  const monthEnd = currentMonth.endOf("month");

  const employeeDepartmentId = departmentId || department?.id || department?.department_id;
  const employeeDepartmentName = departmentName || department?.department_name || (typeof department === "string" ? department : "");
  const waitingForCompany = Boolean(userId && requestingUserId && !topbarCompanyId);
  const departmentUrl = topbarCompanyId && userId && !employeeDepartmentId && employeeDepartmentName && requestingUserId
    ? `${domain}get-department/?user_id=${requestingUserId}&company_id=${topbarCompanyId}`
    : null;
  const { result: departments, loading: departmentsLoading } = useFetch(departmentUrl);
  const resolvingDepartment = Boolean(departmentUrl && departmentsLoading);
  const matchingDepartments = Array.isArray(departments)
    ? departments.filter((entry) => entry.department_name === employeeDepartmentName)
    : [];
  // Use Attendance Timeline's all-departments value when no department resolves.
  const resolvedDepartmentId = employeeDepartmentId ||
    (matchingDepartments.length === 1 ? matchingDepartments[0].id : null) || defaultAllSelection;
  // selected_user_id is the employee whose calendar we actually want (the
  // signed-in user in My Workspace's self-service Apply Leave calendar, or
  // the employee being approved in Team Workspace > Approve). The backend
  // uses selected_user_id alone to return just that employee's record
  // instead of the whole department roster, so we no longer rely on
  // attendanceMap's client-side find() to pick them out of a bigger list,
  // and we don't send user_id on this call in either context.
  // requestingUserId is still required as the gate below (and is used
  // separately by the get-department lookup above) even though it's no
  // longer part of this URL.
  const timelineUrl = topbarCompanyId && userId && requestingUserId && !resolvingDepartment
    ? `${domain}attendance-timeline/?department_id=${encodeURIComponent(resolvedDepartmentId)}&selected_user_id=${encodeURIComponent(userId)}&start_date=${monthStart.format(
        "YYYY-MM-DD",
      )}&end_date=${monthEnd.format("YYYY-MM-DD")}&company_id=${topbarCompanyId}&status=`
    : null;

  const { result: timelineResult, loading } = useFetch(timelineUrl);

  const attendanceMap = useMemo(() => {
    const employeeRecord = Array.isArray(timelineResult?.attendance_data)
      ? timelineResult.attendance_data.find((emp) => String(emp.user_id) === String(userId))
      : null;
    const map = {};
    employeeRecord?.attendance?.forEach((day) => {
      map[day.date] = day;
    });
    return map;
  }, [timelineResult, userId]);

  const gridDays = useMemo(() => {
    const gridStart = monthStart.startOf("week");
    const gridEnd = monthEnd.endOf("week");
    const days = [];
    let cursor = gridStart;
    while (cursor.isBefore(gridEnd) || cursor.isSame(gridEnd, "day")) {
      days.push(cursor);
      cursor = cursor.add(1, "day");
    }
    return days;
  }, [monthStart, monthEnd]);

  const selectedDateSet = useMemo(
    () => new Set(selectedDates.map((d) => d.date)),
    [selectedDates],
  );

  // Some validated dates expand beyond the single day clicked — e.g. sandwich
  // leave pulling in a Sat/Sun that sits between the leave day and the next
  // working day. validateOneDate already returns that wider
  // validatedFromDate/validatedToDate; this turns it into the actual list of
  // days each entry will submit, so the grid and the Selected Dates table can
  // show the real range instead of only the day that was clicked.
  const extendedRangeByEntry = useMemo(() => {
    const map = new Map();
    selectedDates.forEach((entry) => {
      if (!entry.validatedFromDate || !entry.validatedToDate) return;
      const from = dayjs(entry.validatedFromDate);
      const to = dayjs(entry.validatedToDate);
      if (!from.isValid() || !to.isValid() || !from.isBefore(to, "day")) return;
      const days = [];
      let cursor = from;
      while (cursor.isBefore(to, "day") || cursor.isSame(to, "day")) {
        days.push(cursor.format("YYYY-MM-DD"));
        cursor = cursor.add(1, "day");
      }
      map.set(entry.date, days);
    });
    return map;
  }, [selectedDates]);

  // Every day covered by any entry's expanded range, including days that
  // aren't themselves a clicked selection — the grid marks these as
  // "included by policy" so what's highlighted matches what will actually be
  // submitted.
  const coveredDateSet = useMemo(() => {
    const set = new Set();
    extendedRangeByEntry.forEach((days) => days.forEach((day) => set.add(day)));
    return set;
  }, [extendedRangeByEntry]);

  // Two independently-selected dates can each validate fine on their own and
  // still expand into ranges that overlap one another (e.g. two Fridays
  // whose sandwich weekends collide). Catch that here, reactively, instead
  // of only at submit time.
  const rangeOverlapByDate = useMemo(() => {
    const ranges = selectedDates
      .map((entry) => ({
        date: entry.date,
        from: entry.validatedFromDate || entry.date,
        to: entry.validatedToDate || entry.date,
      }))
      .sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
    const overlaps = new Map();
    for (let i = 1; i < ranges.length; i += 1) {
      if (ranges[i].from <= ranges[i - 1].to) {
        const rangeLabel = (range) =>
          range.from === range.to
            ? dayjs(range.from).format("DD MMM")
            : `${dayjs(range.from).format("DD MMM")} – ${dayjs(range.to).format("DD MMM")}`;
        overlaps.set(
          ranges[i].date,
          `Its leave range (${rangeLabel(ranges[i])}) overlaps ${dayjs(ranges[i - 1].date).format("DD MMM")}'s range (${rangeLabel(ranges[i - 1])}). Remove one of these dates.`,
        );
        if (!overlaps.has(ranges[i - 1].date)) {
          overlaps.set(
            ranges[i - 1].date,
            `Its leave range (${rangeLabel(ranges[i - 1])}) overlaps ${dayjs(ranges[i].date).format("DD MMM")}'s range (${rangeLabel(ranges[i])}). Remove one of these dates.`,
          );
        }
      }
    }
    return overlaps;
  }, [selectedDates]);

  // Sync overlapError onto the affected entries (a client-computed sibling of
  // the server-derived validationError) so it blocks submission the same
  // way — Apply.js's calendarDatesInvalid checks both. Guarded by comparing
  // against what's already stored so this converges instead of looping.
  useEffect(() => {
    const needsUpdate = selectedDates.some(
      (entry) => (entry.overlapError || "") !== (rangeOverlapByDate.get(entry.date) || ""),
    );
    if (!needsUpdate) return;
    onChange((previous) =>
      previous.map((entry) => ({
        ...entry,
        overlapError: rangeOverlapByDate.get(entry.date) || "",
      })),
    );
  }, [rangeOverlapByDate, selectedDates, onChange]);

  const leaveYearStart = useMemo(
    () =>
      leaveYearStartDate && dayjs(leaveYearStartDate).isValid()
        ? dayjs(leaveYearStartDate).startOf("month")
        : null,
    [leaveYearStartDate],
  );
  const leaveYearEnd = useMemo(
    () =>
      leaveYearEndDate && dayjs(leaveYearEndDate).isValid()
        ? dayjs(leaveYearEndDate).startOf("month")
        : null,
    [leaveYearEndDate],
  );

  const monthOptions = useMemo(() => {
    if (leaveYearStart && leaveYearEnd && !leaveYearEnd.isBefore(leaveYearStart, "month")) {
      const months = [];
      let cursor = leaveYearStart;
      while (cursor.isBefore(leaveYearEnd, "month") || cursor.isSame(leaveYearEnd, "month")) {
        months.push(cursor);
        cursor = cursor.add(1, "month");
      }
      return months.map((m) => ({ label: m.format("MMMM YYYY"), value: m.format("YYYY-MM") }));
    }
    // No leave year resolved yet (or an invalid range) — fall back to a
    // generic rolling window around today so the picker still works.
    return Array.from({ length: 25 }, (_, i) => dayjs().subtract(12, "month").add(i, "month")).map(
      (m) => ({ label: m.format("MMMM YYYY"), value: m.format("YYYY-MM") }),
    );
  }, [leaveYearStart, leaveYearEnd]);

  // Keep the visible month inside the selected leave year — e.g. switching
  // to a different leave year while browsing a month that only belonged to
  // the previous one should jump the grid back into range.
  useEffect(() => {
    if (!leaveYearStart || !leaveYearEnd) return;
    setCurrentMonth((month) => {
      if (month.isBefore(leaveYearStart, "month")) return leaveYearStart;
      if (month.isAfter(leaveYearEnd, "month")) return leaveYearEnd;
      return month;
    });
  }, [leaveYearStart, leaveYearEnd]);

  const atLeaveYearStart = leaveYearStart ? currentMonth.isSame(leaveYearStart, "month") : false;
  const atLeaveYearEnd = leaveYearEnd ? currentMonth.isSame(leaveYearEnd, "month") : false;

  const handleMonthChange = (event, option) => {
    if (option?.value) {
      setCurrentMonth(dayjs(`${option.value}-01`));
    }
  };

  const toggleDate = useCallback(
    (day) => {
      const dateStr = day.format("YYYY-MM-DD");
      if (selectedDateSet.has(dateStr)) {
        const existing = selectedDates.find((d) => d.date === dateStr);
        // Already submitted (or in flight) — leave it selected instead of
        // letting a click quietly remove a request that's already gone
        // through, or is being submitted right now.
        if (existing?.submissionStatus === "submitted" || existing?.submissionStatus === "submitting") return;
        onChange(selectedDates.filter((d) => d.date !== dateStr));
        return;
      }
      // A day only covered because it falls inside another date's expanded
      // (e.g. sandwich) range isn't independently selectable — it's already
      // part of that other request.
      if (coveredDateSet.has(dateStr)) return;
      const entry = attendanceMap[dateStr] || NOT_MARKED;
      if (isUnavailableLeaveDate(entry)) return;
      const next = [
        ...selectedDates,
        {
          date: dateStr,
          weekday: day.format("ddd"),
          dayType: "full",
          isWeekoffApplied: entry.legend === "W",
          isHolidayApplied: entry.legend === "H",
          originalStatus: entry.status || "Not Marked",
          originalLegend: entry.legend || "",
          originalColor: entry.colour_code || designColors.slate300,
        },
      ];
      next.sort((a, b) => (a.date < b.date ? -1 : 1));
      onChange(next);
      validateSelectedDate(next.find((date) => date.date === dateStr));
    },
    [attendanceMap, coveredDateSet, onChange, selectedDateSet, selectedDates, validateSelectedDate],
  );

  const updateDayType = (dateStr, dayType) => {
    const existing = selectedDates.find((d) => d.date === dateStr);
    if (existing?.submissionStatus === "submitted" || existing?.submissionStatus === "submitting") return;
    onChange((prev) => prev.map((date) => (date.date === dateStr ? { ...date, dayType, validationKey: null } : date)));
    validateSelectedDate({ date: dateStr, dayType });
  };

  const removeDate = (dateStr) => {
    const existing = selectedDates.find((d) => d.date === dateStr);
    if (existing?.submissionStatus === "submitted" || existing?.submissionStatus === "submitting") return;
    onChange(selectedDates.filter((d) => d.date !== dateStr));
  };

  return (
    <Mui.Box>
      <Mui.Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ mb: 1.5 }}
      >
        <Mui.Typography sx={{ fontWeight: 600, fontSize: 15, color: designColors.slate900 }}>
          Select Dates
        </Mui.Typography>
        <Mui.Stack direction="row" alignItems="center" spacing={0.5}>
          <Mui.IconButton
            size="small"
            aria-label="Previous month"
            disabled={atLeaveYearStart}
            onClick={() => setCurrentMonth((m) => m.subtract(1, "month"))}
          >
            <ChevronLeftIcon fontSize="small" />
          </Mui.IconButton>
          <Mui.IconButton
            aria-label="Next month"
            size="small"
            disabled={atLeaveYearEnd}
            onClick={() => setCurrentMonth((m) => m.add(1, "month"))}
          >
            <ChevronRightIcon fontSize="small" />
          </Mui.IconButton>
          <Mui.Box sx={{ minWidth: 190 }}>
            <CustomAutoComplete
              dataList={monthOptions}
              selectedValue={currentMonth.format("MMMM YYYY")}
              updateDataFunction={handleMonthChange}
              placeHolder="Select month"
            />
          </Mui.Box>
        </Mui.Stack>
      </Mui.Stack>

      {!userId ? (
        <Mui.Alert severity="info">Select an employee to see their calendar.</Mui.Alert>
      ) : waitingForCompany || resolvingDepartment || loading ? (
        <Mui.Box role="status" aria-label="Loading attendance calendar" aria-busy="true"
          sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 0.75,
            border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, p: 1.25 }}>
          {WEEKDAY_LABELS.map((label) => (
            <Mui.Typography key={label} align="center" sx={{ fontSize: 12, fontWeight: 600, color: designColors.slate500, pb: 0.5 }}>{label}</Mui.Typography>
          ))}
          {gridDays.map((day) => (
            <Mui.Skeleton key={day.format("YYYY-MM-DD")} animation="wave" variant="rounded" height={52} />
          ))}
        </Mui.Box>
      ) : (
        <>
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 0.75,
              border: `1px solid ${designColors.slate200}`,
              borderRadius: designRadii.card,
              p: 1.25,
            }}
          >
            {WEEKDAY_LABELS.map((label) => (
              <Mui.Typography
                key={label}
                align="center"
                sx={{ fontSize: 12, fontWeight: 600, color: designColors.slate500, pb: 0.5 }}
              >
                {label}
              </Mui.Typography>
            ))}
            {gridDays.map((day) => {
              const dateStr = day.format("YYYY-MM-DD");
              const inMonth = day.isSame(currentMonth, "month");
              const isSelected = selectedDateSet.has(dateStr);
              const selectedEntry = isSelected ? selectedDates.find((d) => d.date === dateStr) : null;
              const isSubmitted = selectedEntry?.submissionStatus === "submitted";
              const isSubmitting = selectedEntry?.submissionStatus === "submitting";
              const isFailed = selectedEntry?.submissionStatus === "failed";
              // Only covered because it falls inside another selected date's
              // expanded (sandwich) range — not itself a clicked selection.
              const isCovered = !isSelected && coveredDateSet.has(dateStr);
              const entry = attendanceMap[dateStr];
              const isUnavailable = isUnavailableLeaveDate(entry);
              const isLocked = isSubmitted || isSubmitting;

              if (!inMonth) {
                return (
                  <Mui.Box key={dateStr} sx={{ borderRadius: 1.5, p: 0.75, minHeight: 52 }}>
                    <Mui.Typography sx={{ fontSize: 12.5, color: designColors.slate300 }}>
                      {day.format("D")}
                    </Mui.Typography>
                  </Mui.Box>
                );
              }

              const calendarStatus = getCalendarStatus(entry);
              const dotColor = calendarStatus?.color || designColors.slate300;
              const bg = isFailed
                ? "#FDECEA"
                : isSelected
                  ? SELECTED_BG
                  : isCovered
                    ? "rgba(20,184,166,0.16)"
                    : calendarStatus && !calendarStatus.hollow
                      ? hexToRgba(calendarStatus.color, 0.12)
                      : designColors.slateBg;
              // No entry and not selected: show just the date number, no
              // placeholder status text/dot (see the Stack below).
              const statusLabel = isSubmitted
                ? "Submitted"
                : isSubmitting
                  ? "Submitting…"
                  : isFailed
                    ? "Failed"
                    : isSelected
                      ? "On Leave"
                      : isCovered
                        ? "Included"
                        : entry ? calendarStatus?.label : undefined;
              const ariaStatus = isSubmitted
                ? "Submitted"
                : isCovered
                  ? "Included in another date's leave range"
                  : entry?.status || "Not Marked";
              const cellDisabled = isUnavailable || isCovered || isLocked;

              return (
                <Mui.Tooltip key={dateStr} arrow slotProps={tooltipStyleProps} title={`${day.format("DD MMM YYYY")}: ${entry?.status || "Not Marked"}${entry?.legend ? ` (${entry.legend})` : ""}${isCovered ? " - Included in another selected date's leave range" : ""}`}>
                  <Mui.Box component="span" sx={{ display: "flex", minWidth: 0 }}>
                    <Mui.Box
                      component="button"
                      type="button"
                      title={isCovered ? "Included in another selected date's leave range (e.g. a sandwiched weekend)" : undefined}
                      aria-label={`${day.format("DD MMM YYYY")}, ${ariaStatus}`}
                      aria-pressed={isSelected}
                      disabled={cellDisabled}
                      onClick={() => toggleDate(day)}
                      sx={{
                        minWidth: 0,
                        width: "100%",
                        font: "inherit",
                        textAlign: "center",
                        borderRadius: 1.5,
                        p: 0.75,
                        minHeight: 52,
                        cursor: cellDisabled ? "not-allowed" : "pointer",
                        "&:disabled": { pointerEvents: "none" },
                        opacity: isUnavailable || isCovered ? 0.6 : 1,
                        background: bg,
                        border: isSelected
                          ? `1px solid ${SELECTED_BORDER}`
                          : isCovered
                            ? `1px dashed ${SELECTED_BORDER}`
                            : `1px solid transparent`,
                        "&:not(:disabled):hover": { outline: `1px solid ${designColors.slate300}` },
                      }}
                    >
                      <Mui.Stack direction="row" justifyContent="center" alignItems="center" spacing={0.3}>
                        <Mui.Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: isSelected || isCovered ? designColors.tealDark : designColors.slate900,
                          }}
                        >
                          {day.format("D")}
                        </Mui.Typography>
                        {isSubmitted ? (
                          <CheckCircleIcon sx={{ fontSize: 13, color: designColors.tealDark }} />
                        ) : isSubmitting ? (
                          <Mui.CircularProgress size={10} sx={{ color: designColors.tealDark }} />
                        ) : isFailed ? (
                          <ErrorOutlineIcon sx={{ fontSize: 13, color: "#B71C1C" }} />
                        ) : null}
                      </Mui.Stack>
                      {statusLabel ? (
                        <Mui.Stack direction="row" justifyContent="center" alignItems="center" spacing={0.4} sx={{ mt: 0.25 }}>
                          {!isSelected && !isCovered ? (
                            <Mui.Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: dotColor,
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                          <Mui.Typography
                            noWrap
                            sx={{
                              fontSize: 10.5,
                              color: isSelected || isCovered ? designColors.tealDark : designColors.slate600,
                            }}
                          >
                            {statusLabel}
                          </Mui.Typography>
                        </Mui.Stack>
                      ) : null}
                    </Mui.Box>
                  </Mui.Box>
                </Mui.Tooltip>
              );
            })}
          </Mui.Box>

        </>
      )}

      {!waitingForCompany && !loading && !resolvingDepartment && <Mui.Box component="ul" aria-label="Attendance status legend"
        sx={{ display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 1.25, listStyle: "none", p: 0, mt: 2, mb: 2 }}>
        {CALENDAR_LEGEND.map((item) => {
          const color = item.color;
          return (
            <Mui.Box component="li" key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Mui.Box aria-hidden="true" sx={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                border: `2px solid ${hexToRgba(color, 0.25)}`, p: "2px", boxSizing: "border-box" }}>
                <Mui.Box sx={{ width: "100%", height: "100%", borderRadius: "50%", bgcolor: item.hollow ? "transparent" : color }} />
              </Mui.Box>
              <Mui.Typography sx={{ fontSize: 12, fontWeight: 500, color: designColors.slate500, whiteSpace: "nowrap" }}>{item.label}</Mui.Typography>
            </Mui.Box>
          );
        })}
      </Mui.Box>}

      <Mui.Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2.5, mb: 1 }}
      >
        <Mui.Typography sx={{ fontWeight: 600, fontSize: 14, color: designColors.slate900 }}>
          Selected Dates ({selectedDates.length})
        </Mui.Typography>
        {selectedDates.length > 0 ? (
          <Mui.Button
            size="small"
            color="error"
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={() =>
              // Never clear a date that's already submitted or mid-submit —
              // only the still-pending ones are actually removable.
              onChange(
                selectedDates.filter(
                  (d) => d.submissionStatus === "submitted" || d.submissionStatus === "submitting",
                ),
              )
            }
          >
            Clear All
          </Mui.Button>
        ) : null}
      </Mui.Stack>

      {selectedDates.length > 0 ? (
        <Mui.TableContainer
          sx={{
            border: `1px solid ${designColors.slate200}`,
            borderRadius: designRadii.card,
            overflowX: "auto",
          }}
        >
          <Mui.Table size="small" sx={{ minWidth: 560 }}>
            <Mui.TableBody>
              {selectedDates.map((d) => {
                const rangeDays = extendedRangeByEntry.get(d.date);
                const isExtended = rangeDays && rangeDays.length > 1;
                const isSubmitted = d.submissionStatus === "submitted";
                const isSubmitting = d.submissionStatus === "submitting";
                const isFailed = d.submissionStatus === "failed";
                const isLocked = isSubmitted || isSubmitting;
                return (
                  <Mui.TableRow key={d.date}>
                    <Mui.TableCell sx={{ whiteSpace: "nowrap" }}>
                      {isExtended ? (
                        <>
                          {dayjs(d.validatedFromDate).format("DD MMM")} –{" "}
                          {dayjs(d.validatedToDate).format("DD MMM YYYY")}
                          <Mui.Typography
                            sx={{ mt: 0.25, fontSize: 11.5, color: designColors.slate500 }}
                          >
                            (extended from {dayjs(d.date).format("DD MMM")})
                          </Mui.Typography>
                        </>
                      ) : (
                        <>
                          {dayjs(d.date).format("DD MMM YYYY")}
                          <Mui.Typography
                            component="span"
                            sx={{ ml: 1, fontSize: 12, color: designColors.slate500 }}
                          >
                            {d.weekday}
                          </Mui.Typography>
                        </>
                      )}
                    </Mui.TableCell>
                    <Mui.TableCell>
                      <Mui.Chip
                        label={attendanceMap[d.date]?.status || d.originalStatus || "Not Marked"}
                        size="small"
                        sx={{
                          backgroundColor: hexToRgba(attendanceMap[d.date]?.colour_code || d.originalColor || designColors.slate400, 0.15),
                          color: attendanceMap[d.date]?.colour_code || d.originalColor || designColors.slate500,
                          fontWeight: 600,
                        }}
                      />
                    </Mui.TableCell>
                    <Mui.TableCell>
                      <Mui.Select
                        size="small"
                        value={d.dayType}
                        disabled={isLocked}
                        inputProps={{ "aria-label": `Leave duration for ${d.date}` }}
                        onChange={(e) => updateDayType(d.date, e.target.value)}
                        sx={{ width: 145, fontSize: 13 }}
                      >
                        {DAY_TYPE_OPTIONS.map((opt) => (
                          <Mui.MenuItem key={opt.value} value={opt.value}>
                            {opt.value === "full" && Number(d.businessDays) === 0 ? "Full Day (Not counted)" : opt.label}
                          </Mui.MenuItem>
                        ))}
                      </Mui.Select>
                    </Mui.TableCell>
                    <Mui.TableCell align="right">
                      {isSubmitted ? (
                        <Mui.Chip
                          size="small"
                          icon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
                          label="Submitted"
                          sx={{ backgroundColor: "#E7F7EF", color: "#12813F", fontWeight: 600 }}
                        />
                      ) : isSubmitting ? null : (
                        <>
                          {isFailed ? (
                            <Mui.Tooltip slotProps={tooltipStyleProps} title="This date failed to submit — you can remove it and try again.">
                              <ErrorOutlineIcon sx={{ fontSize: 16, color: "#B71C1C", mr: 0.5, verticalAlign: "middle" }} />
                            </Mui.Tooltip>
                          ) : null}
                          <Mui.IconButton aria-label={`Remove ${d.date}`} size="small" onClick={() => removeDate(d.date)}>
                            <CloseIcon fontSize="small" />
                          </Mui.IconButton>
                        </>
                      )}
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
      ) : null}
      <Mui.Stack spacing={1} sx={{ mt: 1.5 }} aria-live="polite">
        {selectedDates.map((entry) => {
          const isValidated = entry.validationKey === JSON.stringify([userId, leaveTypeId, entry.date, entry.dayType]);
          const message = isValidated
            ? entry.validationError || (entry.sandwichApplied ? entry.validationMessage || "Sandwich leave policy applies to this date." : "")
            : "";
          const overlapMessage = entry.overlapError || "";
          return (
            <React.Fragment key={entry.date}>
              {overlapMessage ? (
                <Mui.Alert severity="error">
                  {dayjs(entry.date).format("DD MMM YYYY")}: {overlapMessage}
                </Mui.Alert>
              ) : null}
              {message ? (
                <Mui.Alert
                  severity={isValidated && entry.validationError ? "error" : "info"}
                  action={isValidated && entry.validationError ? (
                    <Mui.Button color="inherit" size="small" onClick={() => {
                      validationRequestsRef.current.delete(entry.validationKey);
                      onChange((previous) => previous.map((date) => date.date === entry.date ? { ...date, validationKey: null } : date));
                    }}>Retry</Mui.Button>
                  ) : undefined}
                >
                  {dayjs(entry.date).format("DD MMM YYYY")}: {message}
                </Mui.Alert>
              ) : null}
            </React.Fragment>
          );
        })}
      </Mui.Stack>
    </Mui.Box>
  );
};

export default LeaveDateCalendar;
